import React, { useEffect, useState } from 'react';
import { dashboard } from '../lib/api';

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  const weeks = Math.floor(days / 7);
  if (weeks >= 1)  return `${weeks}w ago`;
  if (days >= 1)   return `${days}d ago`;
  if (hours >= 1)  return `${hours}h ago`;
  if (mins >= 1)   return `${mins}m ago`;
  return 'just now';
}

const CATEGORY_LABELS = {
  hook_strategy:   'Hook Strategy',
  content_format:  'Content Format',
  timing:          'Timing',
  cta_style:       'CTA Style',
  tone:            'Tone',
  caption_length:  'Caption Length',
  cross_platform:  'Cross-Platform',
};

function InsightCard({ insight, index }) {
  const [expanded, setExpanded] = useState(false);
  const conf = insight.confidence;
  const confColor = conf === 'high' ? 'var(--accent-green)'
                  : conf === 'medium' ? 'var(--accent-purple)'
                  : 'var(--text-dim)';

  const weeksBar = Math.min(insight.weeks_confirmed / 8, 1) * 100;

  return (
    <div
      className="card fade-in"
      style={{
        marginBottom: 12,
        animationDelay: `${index * 0.05}s`,
        cursor: 'pointer',
        borderColor: expanded ? 'var(--accent-purple)' : 'var(--border)',
        transition: 'border-color 0.2s'
      }}
      onClick={() => setExpanded(!expanded)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 9, fontFamily: 'var(--font-mono)', padding: '3px 7px',
              borderRadius: 3, background: 'rgba(255,255,255,0.04)',
              color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px'
            }}>
              {CATEGORY_LABELS[insight.category] || insight.category}
            </span>
            {insight.is_new && (
              <span className="badge badge-new">NEW</span>
            )}
            {insight.is_confirmed && (
              <span className="badge badge-high">CONFIRMED</span>
            )}
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.4 }}>
            {insight.headline}
          </div>
          <div style={{ marginTop: 6, fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)',
                        display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {insight.first_seen_week && (
              <span>FIRST DETECTED: WEEK {insight.first_seen_week}</span>
            )}
            {insight.weeks_confirmed > 0 && (
              <span>CONFIRMED OVER {insight.weeks_confirmed}W</span>
            )}
            {timeAgo(insight.updated_at) && (
              <span>UPDATED {timeAgo(insight.updated_at)}</span>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700, color: confColor }}>
            {conf?.toUpperCase()}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
            {insight.weeks_confirmed}w confirmed
          </div>
        </div>
      </div>

      {/* Confidence bar */}
      <div style={{ marginTop: 12, height: 3, background: 'var(--border)', borderRadius: 2 }}>
        <div style={{
          height: '100%', borderRadius: 2,
          width: `${weeksBar}%`,
          background: confColor,
          transition: 'width 0.5s ease'
        }} />
      </div>

      {expanded && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
          <p style={{ color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: 12 }}>
            {insight.body}
          </p>
          <div style={{ display: 'flex', gap: 24, fontSize: 11, color: 'var(--text-dim)',
                        fontFamily: 'var(--font-mono)' }}>
            <span>DATA POINTS: {insight.data_points}</span>
            <span>WEEKS: {insight.weeks_confirmed}</span>
            <span>FIRST SEEN: WEEK {insight.first_seen_week}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Insights() {
  const [insights, setInsights] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('all');

  useEffect(() => {
    dashboard.insights()
      .then(res => setInsights(Array.isArray(res.data) ? res.data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filters = ['all', 'high', 'medium', 'low'];
  const filtered = filter === 'all'
    ? insights
    : insights.filter(i => i.confidence === filter);

  if (loading) return (
    <div style={{ padding: 40, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)',
                  fontSize: 11, letterSpacing: '2px' }} className="pulsing">
      LOADING INSIGHTS...
    </div>
  );

  return (
    <div style={{ padding: 32 }}>
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dim)',
                      letterSpacing: '3px', textTransform: 'uppercase', marginBottom: 6 }}>
          Compounding Intelligence
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 600, color: 'var(--text-primary)' }}>
          Your Insights
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 6 }}>
          Each insight gains confidence the more it's confirmed. High confidence = proven pattern.
        </p>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={filter === f ? 'btn btn-primary' : 'btn btn-ghost'}
            style={{ padding: '6px 14px', fontSize: 11, fontFamily: 'var(--font-mono)',
                     letterSpacing: '1px', textTransform: 'uppercase' }}
          >
            {f} {f !== 'all' && `(${insights.filter(i => i.confidence === f).length})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 60, color: 'var(--text-dim)' }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>◎</div>
          {insights.length === 0 ? (
            <>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '2px' }}>
                NO INSIGHTS YET
              </div>
              <div style={{ fontSize: 13, marginTop: 12, color: 'var(--text-muted)', lineHeight: 1.7, maxWidth: 380, margin: '12px auto 0' }}>
                Run your first sync to begin building intelligence. When there is enough data and context, insights will appear. Until then, we appreciate your patience.
              </div>
            </>
          ) : (
            <>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '2px' }}>
                NO {filter.toUpperCase()} INSIGHTS
              </div>
              <div style={{ fontSize: 12, marginTop: 8 }}>
                Try a different confidence filter.
              </div>
            </>
          )}
        </div>
      ) : (
        filtered.map((insight, i) => (
          <InsightCard key={insight.id} insight={insight} index={i} />
        ))
      )}
    </div>
  );
}
